/**
 * RBAC Types - Role-Based Access Control
 *
 * Defines all types and interfaces for the Role-Based Access Control system
 * Matches the backend Python models for consistency
 */

import type { ApiResponse, PaginatedResponse } from './api';


// ============================================================================
// ENUMS
// ============================================================================

/**
 * User roles in the system with hierarchy
 * Higher level = more permissions
 */
export enum UserRole {
  SUPER_ADMIN = 'super_admin',  // Level 4: Full system access
  ADMIN = 'admin',               // Level 3: Content and user management
  LECTEUR = 'lecteur'            // Level 1: Read-only for assigned classes
}

/**
 * User account status
 */
export enum UserStatus {
  ACTIVE = 'active',
  DISABLED = 'disabled'
}

/**
 * Resource actions for permissions
 */
export enum ResourceAction {
  CREATE = 'create',
  READ = 'read',
  UPDATE = 'update',
  DELETE = 'delete',
  PUBLISH = 'publish',
  MANAGE = 'manage',
  ALL = '*'
}

/**
 * Permission scope
 */
export enum PermissionScope {
  GLOBAL = 'global',                      // Access to all resources
  ASSIGNED_CLASSES = 'assigned_classes'   // Access only to assigned classes
}

/**
 * Audit action types
 */
export enum AuditAction {
  // User management
  USER_CREATE = 'user.create',
  USER_UPDATE = 'user.update',
  USER_DELETE = 'user.delete',
  USER_ASSIGN_CLASSES = 'user.assign_classes',
  USER_CHANGE_ROLE = 'user.change_role',
  USER_DISABLE = 'user.disable',
  USER_ENABLE = 'user.enable',

  // Content management
  CHAPITRE_CREATE = 'chapitre.create',
  CHAPITRE_UPDATE = 'chapitre.update',
  CHAPITRE_DELETE = 'chapitre.delete',
  LECON_CREATE = 'lecon.create',
  LECON_UPDATE = 'lecon.update',
  LECON_DELETE = 'lecon.delete',
  EXERCICE_CREATE = 'exercice.create',
  EXERCICE_UPDATE = 'exercice.update',
  EXERCICE_DELETE = 'exercice.delete',

  // Security
  LOGIN_SUCCESS = 'auth.login_success',
  LOGIN_FAILURE = 'auth.login_failure',
  LOGOUT = 'auth.logout',
  UNAUTHORIZED_ACCESS = 'security.unauthorized_access',
  PERMISSION_DENIED = 'security.permission_denied'
}

/**
 * Audit status
 */
export enum AuditStatus {
  SUCCESS = 'success',
  FAILURE = 'failure'
}

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Admin User
 * Represents an administrative user with role-based permissions
 */
export interface AdminUser {
  id: string;                           // Firebase Auth UID
  email: string;
  displayName: string;
  photoURL?: string;
  role: UserRole;
  assignedClasses?: string[];           // Class IDs for lecteur
  status: UserStatus;
  isReadOnly?: boolean;                 // If true, user cannot modify resources
  canUseAICorrection?: boolean;         // If true, user can use AI-assisted correction features
  createdAt: Date | string;
  createdBy: string;
  updatedAt: Date | string;
  updatedBy?: string;
  lastLogin?: Date | string;
  metadata?: {
    notes?: string;
    ipAddress?: string;
    userAgent?: string;
    // Champs posés par le middleware d'audit du backend
    method?: string;
    endpoint?: string;
    full_url?: string;
    function_name?: string;
    content_type?: string;
    ip_address?: string;
    user_agent?: string;
    required_permission?: string;
    query_params?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

/**
 * Permission definition
 */
export interface Permission {
  resource: string;                     // Resource type (e.g., 'chapitres', 'lecons')
  actions: ResourceAction[];            // Allowed actions
  scope: PermissionScope;               // Global or assigned_classes
}

/**
 * Role Definition
 * Defines permissions and capabilities for each role
 */
export interface RoleDefinition {
  id: string;                           // Role identifier (matches UserRole)
  name: string;                         // Display name
  description: string;                  // Description
  permissions: Permission[];            // List of permissions
  hierarchy: number;                    // Hierarchy level (1-4)
  canManageRoles: UserRole[];          // Roles that can be created/managed
}

/**
 * User Custom Claims (from Firebase Auth token)
 */
export interface UserClaims {
  uid: string;
  email: string;
  role: UserRole;
  classes?: string[];                   // Assigned class IDs
}

/**
 * Audit Log Entry
 */
export interface AuditLog {
  id?: string;
  timestamp: Date | string;
  userId: string;
  userEmail: string;
  userRole: string;
  userDisplayName?: string;  // Nom complet de l'utilisateur (enrichi par l'API)
  action: AuditAction;
  resource: string;
  resourceId?: string;
  changes?: {
    before?: unknown;
    after?: unknown;
    [key: string]: unknown;
  };
  metadata?: {
    ipAddress?: string;
    userAgent?: string;
    // Champs posés par le middleware d'audit du backend
    method?: string;
    endpoint?: string;
    full_url?: string;
    function_name?: string;
    content_type?: string;
    ip_address?: string;
    user_agent?: string;
    required_permission?: string;
    query_params?: Record<string, unknown>;
    [key: string]: unknown;
  };
  status: AuditStatus;
  errorMessage?: string;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

/**
 * API Response for user creation (includes temporary password if generated)
 */
export interface CreateUserResponse extends ApiResponse<AdminUser> {
  temporaryPassword?: string; // For user creation with generated password
  instructions?: string[]; // For user creation instructions
}

/**
 * User List Response
 */
export interface UserListResponse extends PaginatedResponse<AdminUser> {
  count: number;
}

/**
 * User Activity Response
 */
export interface UserActivityResponse extends PaginatedResponse<AuditLog> {
  count: number;
}

/**
 * Audit Statistics
 */
export interface AuditStats {
  total_actions: number;
  by_action: Record<string, number>;
  by_user: Record<string, number>;
  by_resource: Record<string, number>;
  by_status: {
    success: number;
    failure: number;
  };
}

/**
 * Available Roles Response
 */
export interface AvailableRolesResponse extends ApiResponse<RoleDefinition[]> {
  data: RoleDefinition[];
}

// ============================================================================
// FORM TYPES
// ============================================================================

/**
 * Admin User Create/Update Form Data
 */
export interface AdminUserFormData {
  email: string;
  displayName: string;
  role: UserRole;
  password?: string;                    // Optional password (if empty, a temporary one will be generated)
  assignedClasses?: string[];
  status?: UserStatus;
  photoURL?: string;
  metadata?: {
    notes?: string;
    [key: string]: unknown;
  };
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Role Hierarchy Map
 */
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  [UserRole.SUPER_ADMIN]: 4,
  [UserRole.ADMIN]: 3,
  [UserRole.LECTEUR]: 1
};

/**
 * Role Display Names (French)
 */
export const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.SUPER_ADMIN]: 'Super Administrateur',
  [UserRole.ADMIN]: 'Administrateur',
  [UserRole.LECTEUR]: 'Lecteur'
};

/**
 * Role Descriptions (French)
 */
export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  [UserRole.SUPER_ADMIN]: 'Accès complet au système, peut créer des administrateurs',
  [UserRole.ADMIN]: 'Gestion du contenu pédagogique et des utilisateurs lecteurs',
  [UserRole.LECTEUR]: 'Lecture seule du contenu pour les classes assignées'
};

/**
 * Status Display Names (French)
 */
export const STATUS_LABELS: Record<UserStatus, string> = {
  [UserStatus.ACTIVE]: 'Actif',
  [UserStatus.DISABLED]: 'Désactivé'
};

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Check if value is a valid UserRole
 */
export function isUserRole(value: unknown): value is UserRole {
  return (Object.values(UserRole) as unknown[]).includes(value);
}

/**
 * Check if user is an admin (admin or super_admin)
 */
export function isAdmin(role: UserRole): boolean {
  return role === UserRole.ADMIN || role === UserRole.SUPER_ADMIN;
}

/**
 * Check if user is a super admin
 */
export function isSuperAdmin(role: UserRole): boolean {
  return role === UserRole.SUPER_ADMIN;
}

/**
 * Check if manager role can manage target role
 */
export function canManageRole(managerRole: UserRole, targetRole: UserRole): boolean {
  // Super admin can manage all roles
  if (managerRole === UserRole.SUPER_ADMIN) {
    return true;
  }

  // Admin can manage lecteur
  if (managerRole === UserRole.ADMIN && targetRole === UserRole.LECTEUR) {
    return true;
  }

  return false;
}

/**
 * Check if role requires class assignment
 */
export function requiresClassAssignment(role: UserRole): boolean {
  return role === UserRole.LECTEUR;
}

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate AdminUser data
 * @param user - User data to validate
 * @param isEditMode - If true, validates class assignment for roles that require it
 */
export function validateAdminUser(
  user: Partial<AdminUserFormData>,
  isEditMode: boolean = false
): {
  valid: boolean;
  errors: Record<string, string>;
} {
  const errors: Record<string, string> = {};

  if (!user.email) {
    errors.email = 'Email requis';
  } else if (!isValidEmail(user.email)) {
    errors.email = 'Format email invalide';
  }

  if (!user.displayName || user.displayName.trim().length === 0) {
    errors.displayName = 'Nom requis';
  }

  if (!user.role) {
    errors.role = 'Rôle requis';
  } else if (!isUserRole(user.role)) {
    errors.role = 'Rôle invalide';
  }

  // Validate password (optional, but if provided must meet requirements)
  if (user.password && user.password.length > 0) {
    if (user.password.length < 6) {
      errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }
  }

  // Only require class assignment in edit mode
  // In create mode, classes can be assigned after creation
  if (isEditMode && requiresClassAssignment(user.role as UserRole) &&
      (!user.assignedClasses || user.assignedClasses.length === 0)) {
    errors.assignedClasses = 'Au moins une classe doit être assignée pour ce rôle';
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

/**
 * Default AdminUser for forms
 */
export const DEFAULT_ADMIN_USER: AdminUserFormData = {
  email: '',
  displayName: '',
  role: UserRole.LECTEUR,
  password: '',
  assignedClasses: [],
  status: UserStatus.ACTIVE
};
